import { cn } from '@/lib/utils';
import capitalize from 'lodash/capitalize';
import { AlertCircle } from 'lucide-react';
import { useContext, useMemo } from 'react';

import {
  ChainlitContext,
  useChatSession,
  useConfig,
  useAuth
} from '@chainlit/react-client';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

interface Props {
  author?: string;
  hide?: boolean;
  isError?: boolean;
}

const MessageAvatar = ({ author, hide, isError }: Props) => {
  const apiClient = useContext(ChainlitContext);
  const { chatProfile } = useChatSession();
  const { config } = useConfig();
  const { user } = useAuth();

  const selectedChatProfile = useMemo(() => {
    return config?.chatProfiles.find((profile) => profile.name === chatProfile);
  }, [config, chatProfile]);

  const isAssistant = !author || author === config?.ui.name;

  const userDisplayName = user?.display_name || user?.identifier;
  const userInitial = userDisplayName ? capitalize(userDisplayName[0]) : '?';

  const avatarUrl = useMemo(() => {
    if (isAssistant && selectedChatProfile?.icon) {
      return selectedChatProfile.icon;
    }
    if (isAssistant) {
      return '/images/RECAP_Avatar.gif';
    }

    return user?.metadata?.image;
  }, [isAssistant, selectedChatProfile, user]);

  if (isError) {
    return (
      <span className={cn('inline-block', hide && 'invisible')}>
        <AlertCircle className="h-5 w-5 fill-destructive mt-[5px] text-destructive-foreground" />
      </span>
    );
  }
  return (
    <span className={cn('inline-block', hide && 'invisible')}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Avatar className="h-10 w-10 shadow-[0_0_6px_rgba(0,0,0,0.35)]">
              <AvatarImage
                src={avatarUrl}
                alt={`Avatar for ${author || 'default'}`}
                className="bg-transparent"
              />
              <AvatarFallback
                className={cn(
                  isAssistant
                    ? 'bg-transparent'
                    : 'bg-primary text-primary-foreground font-semibold'
                )}
              >
                {isAssistant ? (
                  <Skeleton className="w-full h-full rounded-full" />
                ) : (
                  userInitial
                )}
              </AvatarFallback>
            </Avatar>
          </TooltipTrigger>
          <TooltipContent>
            <p>{author}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </span>
  );
};

export { MessageAvatar };
